import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/** Scroll position per history entry, keyed by react-router's location.key. */
const scrollPositions = new Map();

const ScrollToTop = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(location.key);
  keyRef.current = location.key;
  /** True while we are force-scrolling to a restored position. */
  const restoringRef = useRef(false);

  /**
   * Also set in an inline script in public/index.html, which is what actually
   * beats the browser's own restore. Kept here as a safety net in case the
   * app is ever mounted into a host page without that script.
   */
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  /**
   * Track scroll position continuously for whichever location is current,
   * instead of only reading window.scrollY when navigating away. Leaving a
   * page unmounts its (often much taller) content, and the browser clamps
   * window.scrollY to the new, shorter max height as soon as that happens
   * — so by the time an unmount/cleanup effect runs, the real position is
   * already lost. A live-updated record avoids that.
   */
  useEffect(() => {
    const onScroll = () => {
      // While actively restoring, the scrolls are ours and are being clamped
      // by a page that has not finished growing. Recording them would
      // overwrite the saved position with the clamped value and lose it.
      if (restoringRef.current) return;
      scrollPositions.set(keyRef.current, window.scrollY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /**
   * useLayoutEffect, not useEffect: this runs synchronously after DOM changes
   * but *before* the browser paints, so the corrected scroll position is the
   * first thing rendered. With useEffect the browser paints at the old
   * position first, which shows as a brief flash of the wrong section before
   * the page jumps.
   */
  useLayoutEffect(() => {
    const saved =
      navigationType === "POP" ? scrollPositions.get(location.key) : undefined;
    const target = typeof saved === "number" ? saved : 0;

    window.scrollTo(0, target);

    // Going to the top always succeeds — there is nothing to wait for.
    if (target === 0) return undefined;

    /**
     * Restoring a saved position is different: this runs before paint, when
     * the page is often still empty and therefore too short to scroll that
     * far, so the browser clamps the request. Content then streams in and the
     * document grows. Keep re-applying until the target is actually reached
     * (or a bounded time passes), rather than assuming the first attempt won.
     */
    restoringRef.current = true;

    let rafId;
    const start = performance.now();

    const settle = () => {
      const reached = Math.abs(window.scrollY - target) <= 1;

      if (!reached) window.scrollTo(0, target);

      const done =
        Math.abs(window.scrollY - target) <= 1 ||
        performance.now() - start > 3000;

      if (done) {
        restoringRef.current = false;
      } else {
        rafId = requestAnimationFrame(settle);
      }
    };
    rafId = requestAnimationFrame(settle);

    return () => {
      cancelAnimationFrame(rafId);
      restoringRef.current = false;
    };
  }, [location.pathname, location.key, navigationType]);

  return null;
};

export default ScrollToTop;
